import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import type {
  DayPattern,
  DemandAnalysis,
  DemandStatus,
  HeadwayBand,
  Route,
  ScheduleImportRow,
} from "../../api/types";
import FitChart from "./FitChart";
import HeadwayPlan from "./HeadwayPlan";
import LoadProfile from "./LoadProfile";
import OdMatrix from "./OdMatrix";
import {
  DEFAULT_PLAN,
  type Dir,
  type PlanParams,
  busiestPair,
  fitRows,
  fmtHHMM,
  fmtPct,
  fmtRiders,
  fullWindow,
  headwayFor,
  peakBucket,
  stationLabel,
  summariseWindow,
  tripsPerBucket,
} from "./demandMath";

/** What the timetable generator currently holds, as far as demand cares. */
export interface GeneratorDraft {
  /** The previewed trips; null while the form does not describe a valid timetable. */
  rows: ScheduleImportRow[] | null;
  bands: HeadwayBand[];
  serviceStartMin: number | null;
  serviceEndMin: number | null;
  roundTripMin: number | null;
}

/** How the generator's draft stands against ridership, for the generator's own summary line. */
export interface DraftFit {
  bucketMin: number;
  /** Time slices inside the service window with riders in either direction. */
  buckets: number;
  /** Of those, the ones where a direction carries more than its trips can hold. */
  overBuckets: number;
  /**
   * The slice and direction leaving the most riders without room (or, if
   * none, the fullest). Null when no rider travels inside the service window.
   */
  worst: {
    startMin: number;
    dirName: string;
    peak: number;
    trips: number;
    loadFactor: number | null;
  } | null;
  /**
   * Riders a day on trips that would leave before the service starts or
   * after it ends. Not a capacity problem — there is no service to be full —
   * so they are reported apart from it.
   */
  ridersBefore: number;
  ridersAfter: number;
  serviceStartMin: number;
  serviceEndMin: number;
}

interface Props {
  routes: Route[];
  routeId: string;
  onRouteId: (id: string) => void;
  dayPattern: DayPattern;
  onDayPattern: (p: DayPattern) => void;
  dayPatterns: Array<{ value: DayPattern; label: string }>;
  /** Look-back in days the averages are taken over; shared with the simulation. */
  days: number;
  onDays: (days: number) => void;
  /** Planning inputs (bus capacity, target load, headway limits); shared with the simulation. */
  plan: PlanParams;
  onPlan: (p: PlanParams) => void;
  /** Rows of the timetable running today for this route and day pattern, when there is one. */
  activeRows: ScheduleImportRow[] | null;
  /** Null for a viewer who cannot edit schedules (no generator on the page). */
  draft: GeneratorDraft | null;
  canManage: boolean;
  onApplyBands: (bands: HeadwayBand[]) => void;
  onDraftFit?: (fit: DraftFit | null) => void;
}

export const PERIODS = [
  { days: 14, label: "Last 2 weeks" },
  { days: 28, label: "Last 4 weeks" },
  { days: 56, label: "Last 8 weeks" },
  { days: 84, label: "Last 12 weeks" },
];

const WINDOWS: Array<{ label: string; range: [number, number] }> = [
  { label: "Morning peak", range: [360, 600] },
  { label: "Midday", range: [600, 900] },
  { label: "Evening peak", range: [900, 1200] },
  { label: "Late", range: [1200, 1440] },
];

const PLAN_KEY = "occ.schedules.demandPlan";

/** The planning inputs last used in this browser, or the defaults. */
export function loadPlan(): PlanParams {
  try {
    const raw = localStorage.getItem(PLAN_KEY);
    if (!raw) return DEFAULT_PLAN;
    const p = JSON.parse(raw) as Partial<PlanParams>;
    const num = (v: unknown, fallback: number) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : fallback);
    return {
      busCapacity: num(p.busCapacity, DEFAULT_PLAN.busCapacity),
      targetLoad: num(p.targetLoad, DEFAULT_PLAN.targetLoad),
      minHeadway: num(p.minHeadway, DEFAULT_PLAN.minHeadway),
      maxHeadway: num(p.maxHeadway, DEFAULT_PLAN.maxHeadway),
    };
  } catch {
    return DEFAULT_PLAN;
  }
}

/** Remember the planning inputs for next time; a private window simply forgets. */
export function savePlan(plan: PlanParams): void {
  try { localStorage.setItem(PLAN_KEY, JSON.stringify(plan)); } catch { /* private mode */ }
}

function fmtDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function ago(iso?: string): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "never";
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

export default function DemandCard({
  routes, routeId, onRouteId, dayPattern, onDayPattern, dayPatterns, days, onDays, plan, onPlan, activeRows, draft,
  canManage, onApplyBands, onDraftFit,
}: Props) {
  const [bucket, setBucket] = useState<60 | 30>(60);
  const [demand, setDemand] = useState<DemandAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<DemandStatus | null>(null);
  const [reload, setReload] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [win, setWin] = useState<[number, number] | null>(null);
  const [compare, setCompare] = useState<"active" | "draft" | null>(null);

  // ── data ────────────────────────────────────────────────────────────────
  const loadStatus = useCallback(() => {
    api.getDemandStatus().then(setStatus).catch(() => setStatus(null));
  }, []);
  useEffect(loadStatus, [loadStatus]);

  // The latest request wins: route, pattern and period can all change while
  // an earlier answer is still on its way.
  const requestSeq = useRef(0);
  useEffect(() => {
    if (!routeId) { setDemand(null); return; }
    const seq = ++requestSeq.current;
    setLoading(true);
    api.getDemand({ route_id: routeId, day_pattern: dayPattern, days, bucket })
      .then((res) => {
        if (seq !== requestSeq.current) return;
        setDemand(res);
        setError(null);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setDemand(null);
        setError(err instanceof Error ? err.message : "Could not load ridership demand");
      })
      .finally(() => { if (seq === requestSeq.current) setLoading(false); });
  }, [routeId, dayPattern, days, bucket, reload]);

  // A window chosen on one route or resolution may not exist on the next.
  useEffect(() => { setWin(null); }, [routeId, bucket]);

  const haveActive = !!activeRows;
  const haveDraft = !!draft?.rows;
  // Start from what is actually running; the draft is an arbitrary default
  // until the planner has touched it.
  const source: "active" | "draft" | null =
    compare === "draft" && haveDraft ? "draft"
    : compare === "active" && haveActive ? "active"
    : haveActive ? "active"
    : haveDraft ? "draft"
    : null;
  const supplyRows = source === "active" ? activeRows! : source === "draft" ? draft!.rows! : null;
  const supplyLabel = source === "active" ? "active timetable" : "draft timetable";

  // ── derived ─────────────────────────────────────────────────────────────
  const hasData = !!demand && demand.period.days_included > 0 && demand.buckets.length > 0;

  const dirNames = useMemo<Record<Dir, string>>(() => {
    const stops = demand?.stops ?? [];
    if (stops.length < 2) return { upward: "upward", downward: "downward" };
    return {
      upward: `to ${stationLabel(stops[stops.length - 1].name)}`,
      downward: `to ${stationLabel(stops[0].name)}`,
    };
  }, [demand]);

  const trips = useMemo(
    () => (supplyRows && demand ? tripsPerBucket(supplyRows, demand.bucket_minutes) : null),
    [supplyRows, demand]
  );
  const rows = useMemo(
    () => (demand ? fitRows(demand.buckets, trips, plan.busCapacity) : []),
    [demand, trips, plan.busCapacity]
  );

  // The draft against demand, whatever the chart is currently comparing with:
  // the generator shows this next to the bands being edited.
  const draftFit = useMemo<DraftFit | null>(() => {
    if (!demand || !hasData || !draft?.rows) return null;
    const draftRows = fitRows(demand.buckets, tripsPerBucket(draft.rows, demand.bucket_minutes), plan.busCapacity);
    const start = draft.serviceStartMin ?? 0;
    const end = draft.serviceEndMin ?? 1440;
    let buckets = 0;
    let overBuckets = 0;
    let worst: DraftFit["worst"] = null;
    let worstExcess = -Infinity;
    let worstLoad = -Infinity;
    let ridersBefore = 0;
    let ridersAfter = 0;
    for (const r of draftRows) {
      // A slice belongs to the service day when any of it falls inside the window.
      if (r.startMin + demand.bucket_minutes <= start) {
        ridersBefore += r.upward.boardings + r.downward.boardings;
        continue;
      }
      if (r.startMin >= end) {
        ridersAfter += r.upward.boardings + r.downward.boardings;
        continue;
      }
      let any = false;
      let over = false;
      for (const dir of ["upward", "downward"] as Dir[]) {
        const side = r[dir];
        if (side.peak < 1) continue;
        any = true;
        const excess = side.peak - side.capacity;
        if (excess > 0.5) over = true;
        // Rank by riders left without room; when nothing is over, by how full.
        const load = side.loadFactor ?? Infinity;
        const better = excess > 0.5 || worstExcess > 0.5 ? excess > worstExcess : load > worstLoad;
        if (better) {
          worstExcess = excess;
          worstLoad = load;
          worst = { startMin: r.startMin, dirName: dirNames[dir], peak: side.peak, trips: side.trips, loadFactor: side.loadFactor };
        }
      }
      if (any) buckets++;
      if (over) overBuckets++;
    }
    return {
      bucketMin: demand.bucket_minutes, buckets, overBuckets, worst,
      ridersBefore, ridersAfter, serviceStartMin: start, serviceEndMin: end,
    };
  }, [demand, hasData, draft, plan.busCapacity, dirNames]);
  useEffect(() => { onDraftFit?.(draftFit); }, [draftFit, onDraftFit]);

  const range = useMemo<[number, number]>(() => (demand ? fullWindow(demand) : [0, 1440]), [demand]);
  const activeWin = win ?? range;
  const summary = useMemo(
    () => (demand ? summariseWindow(demand, activeWin[0], activeWin[1]) : null),
    [demand, activeWin]
  );
  const windowLabel = win
    ? `trips leaving ${fmtHHMM(win[0])}–${fmtHHMM(win[1])}`
    : "all day";

  const peak = useMemo(() => (demand ? peakBucket(demand.buckets) : null), [demand]);
  const pair = useMemo(() => (demand ? busiestPair(demand.matrix) : null), [demand]);

  // The service window the plan covers: the generator's when it has one,
  // otherwise the hours riders actually travel.
  const serviceStart = draft?.serviceStartMin ?? Math.floor(range[0] / 60) * 60;
  const serviceEnd = draft?.serviceEndMin ?? Math.ceil(range[1] / 60) * 60;

  async function syncNow() {
    setSyncing(true);
    setSyncNote(null);
    try {
      const res = await api.syncDemand();
      setStatus(res.status);
      setSyncNote(res.report.skipped ? "A sync is already running." : `Pulled ${fmtRiders(res.report.journeys)} journeys.`);
      setReload((n) => n + 1);
    } catch (err) {
      setSyncNote(err instanceof Error ? err.message : "Sync failed");
      loadStatus();
    } finally {
      setSyncing(false);
    }
  }

  async function mapStation(afcStationId: number, stopId: string) {
    try {
      await api.mapDemandStation(afcStationId, stopId || null);
      loadStatus();
      setReload((n) => n + 1);
    } catch (err) {
      setSyncNote(err instanceof Error ? err.message : "Could not save the station's stop");
    }
  }

  const patternLabel = dayPatterns.find((p) => p.value === dayPattern)?.label ?? dayPattern;
  const excluded = demand?.period.days.filter((d) => !d.included) ?? [];
  const ungated = demand?.stops.filter((s) => !s.has_exit_taps) ?? [];

  return (
    <section className="card dm-card" id="schedule-demand" aria-busy={loading}>
      <div className="dm-head">
        <div>
          <h3>Ridership demand</h3>
          <p className="secondary-text">
            Who travels where and when, from fare-system taps and ticket sales — what a timetable has to carry.
          </p>
        </div>
        <div className="dm-source">
          {status && (
            <span className={`dm-source-chip${status.last_error ? " dm-source-chip-warn" : ""}`}
              title={status.last_error ? `Last sync failed: ${status.last_error}` : undefined}>
              {!status.configured
                ? "Fare system not connected"
                : status.last_error
                  ? `Sync failing · data as of ${ago(status.last_success_at)}`
                  : `Fare system · synced ${ago(status.last_success_at)}`}
            </span>
          )}
          {canManage && status?.configured && (
            <button type="button" className="btn" onClick={syncNow} disabled={syncing}>
              {syncing ? "Syncing…" : "Sync now"}
            </button>
          )}
        </div>
      </div>

      {/* One filter row; everything below it is drawn from the same slice. */}
      <div className="dm-filters">
        <label className="sched-field dm-filter-route">
          <span>Route</span>
          <select value={routeId} onChange={(e) => onRouteId(e.target.value)}>
            {routes.length === 0 && <option value="">— no routes —</option>}
            {routes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.route_short_name || r.route_id}{r.route_long_name ? ` — ${r.route_long_name}` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="sched-field">
          <span>Day pattern</span>
          <select value={dayPattern} onChange={(e) => onDayPattern(e.target.value as DayPattern)}>
            {dayPatterns.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <label className="sched-field">
          <span>Period</span>
          <select value={days} onChange={(e) => onDays(Number(e.target.value))}>
            {PERIODS.map((p) => <option key={p.days} value={p.days}>{p.label}</option>)}
          </select>
        </label>
        <div className="sched-field">
          <span>Time slices</span>
          <div className="sched-segmented">
            <button type="button" className={bucket === 60 ? "active" : ""} onClick={() => setBucket(60)}>Hourly</button>
            <button type="button" className={bucket === 30 ? "active" : ""} onClick={() => setBucket(30)}>30 min</button>
          </div>
        </div>
        {haveActive && haveDraft && (
          <div className="sched-field">
            <span>Compare with</span>
            <div className="sched-segmented">
              <button type="button" className={source === "active" ? "active" : ""} onClick={() => setCompare("active")}>Active timetable</button>
              <button type="button" className={source === "draft" ? "active" : ""} onClick={() => setCompare("draft")}>Draft</button>
            </div>
          </div>
        )}
      </div>

      {syncNote && <div className="dm-note">{syncNote}</div>}
      {error && <div className="banner banner-error">{error}</div>}

      {loading && !demand && !error && <div className="dm-empty">Loading ridership…</div>}

      {!error && !hasData && !loading && (
        <div className="dm-empty">
          {!routeId ? (
            "Pick a route to see its ridership."
          ) : status && !status.configured ? (
            <>
              <strong>The fare system is not connected to this deployment.</strong>
              <p>Set <code>FMS_AFC_BASE_URL</code> on the scheduler and API gateway to mirror ridership here.</p>
            </>
          ) : status && status.days === 0 ? (
            <>
              <strong>No ridership has been mirrored yet.</strong>
              <p>
                {status.last_error
                  ? `The last sync failed: ${status.last_error}`
                  : "The scheduler pulls it from the fare system shortly after it starts."}
              </p>
            </>
          ) : (
            <>
              <strong>No {patternLabel} ridership on this route in the {PERIODS.find((p) => p.days === days)?.label.toLowerCase()}.</strong>
              <p>Try a longer period or another day pattern.</p>
            </>
          )}
        </div>
      )}

      {hasData && demand && summary && (
        // While a new slice loads the previous one stays in place, dimmed: no
        // skeleton, no jump.
        <div className={`dm-body${loading ? " dm-stale" : ""}`}>
          <div className="dm-kpis">
            <div className="dm-kpi">
              <div className="dm-kpi-label">Riders a day</div>
              <div className="dm-kpi-value">{fmtRiders(demand.totals.journeys)}</div>
              <div className="dm-kpi-foot">
                average of {demand.period.days_included} {patternLabel} day{demand.period.days_included === 1 ? "" : "s"},{" "}
                {fmtDay(demand.period.from)} – {fmtDay(demand.period.to)}
              </div>
            </div>
            {peak && (
              <div className="dm-kpi">
                <div className="dm-kpi-label">Busiest {demand.bucket_minutes === 60 ? "hour" : "half hour"}</div>
                <div className="dm-kpi-value">{fmtRiders(peak.load)}<span className="dm-kpi-unit">riders aboard</span></div>
                <div className="dm-kpi-foot">
                  <span className={`dm-swatch dm-swatch-${peak.dir}`} aria-hidden />{" "}
                  {fmtHHMM(peak.startMin)}–{fmtHHMM(peak.startMin + demand.bucket_minutes)} {dirNames[peak.dir]},{" "}
                  {stationLabel(demand.stops[peak.link]?.name ?? "")} – {stationLabel(demand.stops[peak.link + 1]?.name ?? "")}
                </div>
              </div>
            )}
            {peak && (() => {
              const need = headwayFor(peak.load * (60 / demand.bucket_minutes), plan);
              const side = rows.find((r) => r.startMin === peak.startMin)?.[peak.dir];
              return (
                <div className="dm-kpi">
                  <div className="dm-kpi-label">That needs a bus every</div>
                  <div className="dm-kpi-value">{need.headway}<span className="dm-kpi-unit">min</span></div>
                  <div className="dm-kpi-foot">
                    {side && source
                      ? side.trips === 0
                        ? <span className="dm-flag">▲ the {supplyLabel} runs no trip then</span>
                        : <>
                            the {supplyLabel} runs {side.trips} trip{side.trips === 1 ? "" : "s"} then
                            {side.loadFactor != null && side.loadFactor > 1
                              ? <span className="dm-flag"> · ▲ {fmtPct(side.loadFactor)} full</span>
                              : side.loadFactor != null ? ` · ${fmtPct(side.loadFactor)} full` : ""}
                          </>
                      : `at ${fmtPct(plan.targetLoad)} of a ${plan.busCapacity}-rider bus`}
                  </div>
                </div>
              );
            })()}
            {pair && (
              <div className="dm-kpi">
                <div className="dm-kpi-label">Busiest journey</div>
                <div className="dm-kpi-value">{fmtRiders(pair.riders)}<span className="dm-kpi-unit">a day</span></div>
                <div className="dm-kpi-foot">
                  {stationLabel(demand.stops[pair.o].name)} → {stationLabel(demand.stops[pair.d].name)}
                </div>
              </div>
            )}
          </div>

          <div className="dm-cols dm-cols-fit">
            <FitChart demand={demand} rows={rows} hasSupply={!!source} supplyLabel={supplyLabel}
              plan={plan} dirNames={dirNames} window={win}
              onPickBucket={(start) =>
                setWin((w) => (w && w[0] === start && w[1] === start + demand.bucket_minutes ? null : [start, start + demand.bucket_minutes]))
              } />
            <HeadwayPlan demand={demand} plan={plan} onPlan={onPlan}
              serviceStartMin={serviceStart} serviceEndMin={serviceEnd}
              currentBands={draft?.bands ?? null} roundTripMin={draft?.roundTripMin ?? null}
              onApply={canManage && draft ? (bands) => { setCompare("draft"); onApplyBands(bands); } : null} />
          </div>

          <div className="dm-window" role="group" aria-label="Time of day shown below">
            <span className="dm-window-label">Matrix and load profile for</span>
            <button type="button" className={`dm-chip${win ? "" : " active"}`} onClick={() => setWin(null)}>All day</button>
            {WINDOWS.filter((w) => w.range[0] < range[1] && w.range[1] > range[0]).map((w) => (
              <button key={w.label} type="button"
                className={`dm-chip${win && win[0] === w.range[0] && win[1] === w.range[1] ? " active" : ""}`}
                onClick={() => setWin(w.range)}>
                {w.label} <span className="dm-chip-sub">{fmtHHMM(w.range[0])}–{fmtHHMM(w.range[1] % 1440)}</span>
              </button>
            ))}
            {win && !WINDOWS.some((w) => w.range[0] === win[0] && w.range[1] === win[1]) && (
              <span className="dm-chip active">{fmtHHMM(win[0])}–{fmtHHMM(win[1])}</span>
            )}
            <span className="dm-window-total">{fmtRiders(summary.total)} riders</span>
          </div>

          <div className="dm-cols dm-cols-od">
            <OdMatrix demand={demand} matrix={summary.matrix} dirNames={dirNames} windowLabel={windowLabel} />
            <LoadProfile demand={demand} loads={summary.loads} dirNames={dirNames} windowLabel={windowLabel} />
          </div>

          <details className="dm-about">
            <summary>About these figures</summary>
            <ul>
              <li>
                <strong>{fmtPct(demand.totals.journeys > 0 ? demand.totals.observed / demand.totals.journeys : 0)} of journeys have both ends on record</strong>{" "}
                — an exit tap, with the origin read from the entry tap or from the ticket. The other{" "}
                {fmtRiders(demand.totals.inferred)} a day have no recorded exit and were given a destination
                {ungated.length > 0 ? (
                  <>
                    : {ungated.map((s) => stationLabel(s.name)).join(" and ")} report{ungated.length === 1 ? "s" : ""} no
                    exit taps, so journeys ending there are estimated from the riders seen coming the other way.
                  </>
                ) : ", in the proportions their stop's other riders travel."}
                {" "}Cells marked <span className="dm-est">~</span> in the matrix are mostly estimated.
              </li>
              <li>
                <strong>Times are trip departures.</strong> A rider is counted on the trip they boarded, at the
                time it left its first stop, so demand lines up with the timetable's start times.
              </li>
              <li>
                <strong>Averages are over {demand.period.days_included} service days.</strong>
                {excluded.length > 0 && (
                  <>
                    {" "}Left out:{" "}
                    {excluded.map((d) => `${fmtDay(d.date)} (${d.reason === "low" ? "unusually quiet" : "no data"})`).join(", ")}.
                  </>
                )}
                {status?.earliest_date && <> Ridership is on record from {fmtDay(status.earliest_date)}.</>}
              </li>
              {demand.totals.same_stop >= 1 && (
                <li>{fmtRiders(demand.totals.same_stop)} journeys a day enter and leave at the same station and are not counted.</li>
              )}
              {demand.unmapped_stations.length > 0 && (
                <li className="dm-flag">
                  ▲ {demand.unmapped_stations.map((s) => `${s.name || `station ${s.afc_station_id}`} (${fmtRiders(s.journeys_per_day)} a day)`).join(", ")}{" "}
                  {demand.unmapped_stations.length === 1 ? "is a fare-system station" : "are fare-system stations"} not placed at a stop,
                  so {demand.unmapped_stations.length === 1 ? "its" : "their"} riders are missing above.
                </li>
              )}
            </ul>

            {status && status.stations.length > 0 && (
              <details className="dm-mapping">
                <summary>Fare-system stations and the stops they stand for</summary>
                <table className="data-table compact dm-table">
                  <thead>
                    <tr><th>Fare-system station</th><th>Line</th><th>Stop on the network</th></tr>
                  </thead>
                  <tbody>
                    {status.stations.map((st) => (
                      <tr key={st.afc_station_id}>
                        <td>{st.station_no ? `${st.station_no} · ` : ""}{st.name}</td>
                        <td>{st.line_name ?? "—"}</td>
                        <td>
                          {canManage ? (
                            <select value={st.stop_id ?? ""} aria-label={`Stop for ${st.name}`}
                              onChange={(e) => mapStation(st.afc_station_id, e.target.value)}>
                              <option value="">— not placed —</option>
                              {status.stops.map((s) => (
                                <option key={s.stop_id} value={s.stop_id}>{s.name} · {s.route_name}</option>
                              ))}
                            </select>
                          ) : (
                            st.stop_name ?? <span className="dm-flag">not placed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </details>
        </div>
      )}

    </section>
  );
}
